import React from 'react';
import { Box, Typography } from '@mui/material';
import { ReaderData } from '../../api/PublishedEditions';

interface Props {
  data: ReaderData;
  onStoryClick: (index: number) => void;
}

export default function CoverPage({ data, onStoryClick }: Props) {
  return (
    <Box sx={{
      minHeight: '100vh',
      background: '#faf7f0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      px: 3,
      py: 4,
    }}>
      {/* Masthead */}
      <Box sx={{
        textAlign: 'center',
        borderBottom: '4px double #1a1a1a',
        pb: 2,
        mb: 2,
        width: '100%',
        maxWidth: 500,
      }}>
        <Typography
          sx={{
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontWeight: 700,
            fontSize: { xs: '2rem', sm: '2.5rem' },
            lineHeight: 1.1,
            color: '#1a1a1a',
            letterSpacing: '0.02em',
          }}
        >
          {data.newspaper_name}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Georgia", serif',
            fontSize: '1rem',
            fontStyle: 'italic',
            color: '#666',
            mt: 0.5,
          }}
        >
          {data.edition_name}
        </Typography>
        {data.published_at && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Georgia", serif' }}>
            {data.published_at}
          </Typography>
        )}
      </Box>

      {/* AI Headline */}
      {data.ai_headline && (
        <Typography
          sx={{
            fontFamily: '"Georgia", serif',
            fontWeight: 700,
            fontSize: { xs: '1.3rem', sm: '1.6rem' },
            textAlign: 'center',
            color: '#1a1a1a',
            maxWidth: 500,
            lineHeight: 1.25,
            mb: 2,
          }}
        >
          {data.ai_headline}
        </Typography>
      )}

      {/* AI Editor's Note */}
      {data.ai_editors_note && (
        <Box sx={{
          maxWidth: 500,
          width: '100%',
          background: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}>
          <Typography variant="overline" sx={{ color: '#999', letterSpacing: '0.1em', fontSize: '0.6rem' }}>
            From the Editor
          </Typography>
          <Typography sx={{
            fontFamily: '"Georgia", serif',
            fontSize: '0.9rem',
            lineHeight: 1.6,
            color: '#444',
            fontStyle: 'italic',
          }}>
            {data.ai_editors_note}
          </Typography>
        </Box>
      )}

      {/* Swipe hint */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: '"Georgia", serif' }}>
        {data.story_count} stories &middot; Swipe to read &rarr;
      </Typography>

      {/* Table of contents with teasers */}
      <Box sx={{
        width: '100%',
        maxWidth: 500,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}>
        {data.stories.map((story, index) => (
          <Box
            key={story.id}
            onClick={() => onStoryClick(index + 1)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              p: 1.5,
              borderRadius: 1,
              cursor: 'pointer',
              background: story.is_editor_pick ? '#fff8e1' : '#fff',
              border: story.is_editor_pick ? '1px solid #ffd54f' : '1px solid #e0e0e0',
              '&:hover': { background: story.is_editor_pick ? '#fff3c4' : '#f5f5f5' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{
                fontFamily: '"Georgia", serif',
                fontSize: '0.7rem',
                color: '#999',
                minWidth: 20,
              }}>
                {index + 1}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ fontFamily: '"Georgia", serif' }}>
                    {story.title}
                  </Typography>
                  {story.is_editor_pick && (
                    <span style={{ fontSize: '0.75rem' }}>&#11088;</span>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  By {story.student_name} &middot; {story.template_label}
                </Typography>
              </Box>
            </Box>
            {/* AI Teaser */}
            {story.teaser && (
              <Typography sx={{
                fontFamily: '"Georgia", serif',
                fontSize: '0.8rem',
                color: '#666',
                fontStyle: 'italic',
                pl: '28px',
                lineHeight: 1.4,
              }}>
                {story.teaser}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* View count */}
      <Typography variant="caption" color="text.disabled" sx={{ mt: 4, fontFamily: '"Georgia", serif' }}>
        {data.view_count} views
      </Typography>
    </Box>
  );
}
